import type { ReactNode } from "react";

import { cx } from "@/lib/cx";

/**
 * The glyphs the overlay layer and the shared components need.
 *
 * Inline rather than an icon package: the app already draws its icons this way
 * (each feature has its own `icons.tsx`), they inherit `currentColor` so they
 * follow whatever tone the surrounding control has, and there is no runtime
 * cost.
 *
 * Two families, because iOS uses two:
 *
 * - **{@link Stroke}** — 20×20, 1.6px round-capped strokes. Everything that sits
 *   beside text: chevrons, the menu icons, the toolbar actions. SF Symbols at
 *   `regular` weight are stroked at roughly this ratio, which is why a 1px
 *   hairline icon next to 15px body text reads as a different family.
 * - **{@link Solid}** — 20×20 filled. Only for the marks that must read at a
 *   glance without a label: the toast status glyph and the ⊗ that removes an
 *   attachment. A filled glyph is the iOS convention for "this is a state", a
 *   stroked one for "this is an action".
 *
 * Sizes are set by the caller with `size-*`; the default is 16px so a glyph
 * lines up with `text-body` without any vertical nudging.
 */

type IconProps = { className?: string };

function Stroke({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx("size-4 shrink-0", className)}
    >
      {children}
    </svg>
  );
}

function Solid({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={cx("size-4 shrink-0", className)}
    >
      {children}
    </svg>
  );
}

/* --- Chevrons ------------------------------------------------------------- */

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="m5.5 8 4.5 4.5L14.5 8" />
    </Stroke>
  );
}

export function ChevronUpIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="m5.5 12 4.5-4.5 4.5 4.5" />
    </Stroke>
  );
}

export function ChevronLeftIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M12 4.5 7 10l5 5.5" />
    </Stroke>
  );
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="m8 4.5 5 5.5-5 5.5" />
    </Stroke>
  );
}

/**
 * The disclosure chevron — `›` at 12×20, the exact iOS proportion.
 *
 * Deliberately *not* {@link ChevronRightIcon} at a smaller size: the row
 * chevron is taller than it is wide and drawn with a heavier relative stroke, so
 * scaling the square navigation chevron down to 12px gives a chevron that is
 * both too short and too thin. It carries meaning — "this row opens something"
 * (IOS-DESIGN §5.4) — so it must not be used decoratively.
 */
export function DisclosureIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 12 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx("h-5 w-3 shrink-0", className)}
    >
      <path d="m4 5 5 5-5 5" />
    </svg>
  );
}

/**
 * `chevron.up.chevron.down` — the glyph iOS puts on a button that opens a menu
 * of choices, and the single clearest signal that a control is a picker rather
 * than a text field. Used by {@link Select}.
 */
export function SelectorIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="m6.5 8.25 3.5-3.5 3.5 3.5" />
      <path d="m6.5 11.75 3.5 3.5 3.5-3.5" />
    </Stroke>
  );
}

/* --- Marks ---------------------------------------------------------------- */

export function CheckIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="m4.5 10.5 3.5 3.5 7.5-8" />
    </Stroke>
  );
}

export function CloseIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="m5.5 5.5 9 9M14.5 5.5l-9 9" />
    </Stroke>
  );
}

/**
 * The filled ⊗. iOS uses this for "remove this one thing" — the badge on a photo
 * tile, the clear button in a search field — and it is deliberately solid so it
 * reads at 20px against a photograph, where a stroked ✕ disappears.
 */
export function CloseCircleIcon({ className }: IconProps) {
  return (
    <Solid className={className}>
      <path
        fillRule="evenodd"
        d="M10 1.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8Zm3.05 4.53a.85.85 0 0 1 1.2 1.2L11.2 10l3.05 3.05a.85.85 0 1 1-1.2 1.2L10 11.2l-3.05 3.05a.85.85 0 0 1-1.2-1.2L8.8 10 5.75 6.95a.85.85 0 0 1 1.2-1.2L10 8.8l3.05-3.05Z"
        clipRule="evenodd"
      />
    </Solid>
  );
}

/** Filled tick in a disc — the toast's "that worked" mark. */
export function CheckCircleIcon({ className }: IconProps) {
  return (
    <Solid className={className}>
      <path
        fillRule="evenodd"
        d="M10 1.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8Zm4.14 5.6a.85.85 0 0 0-1.28-1.12l-4.2 4.8-1.55-1.55a.85.85 0 0 0-1.2 1.2l2.2 2.2a.85.85 0 0 0 1.24-.04l4.79-5.49Z"
        clipRule="evenodd"
      />
    </Solid>
  );
}

/** Filled exclamation in a disc — the toast's failure mark. */
export function AlertCircleIcon({ className }: IconProps) {
  return (
    <Solid className={className}>
      <path
        fillRule="evenodd"
        d="M10 1.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8Zm0 3.6a.9.9 0 0 0-.9.95l.25 4.4a.65.65 0 0 0 1.3 0l.25-4.4A.9.9 0 0 0 10 5.2Zm0 8.9a1.05 1.05 0 1 0 0-2.1 1.05 1.05 0 0 0 0 2.1Z"
        clipRule="evenodd"
      />
    </Solid>
  );
}

/** Stroked warning triangle, for an inline problem next to text. */
export function WarningIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M10 3.5 18 17H2z" />
      <path d="M10 8.5v3.5" />
      <path d="M10 14.4h.01" />
    </Stroke>
  );
}

/** A run of days, used by compact streak summaries. */
export function FlameIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M10 2.5c.4 2.4-.9 3.6-2.2 4.9C6.4 8.8 5 10.2 5 12.3a5 5 0 0 0 10 0c0-1.7-.7-3-1.6-4.2-.5.8-1.1 1.3-1.8 1.5.4-2.6-.3-5.1-1.6-7.1Z" />
    </Stroke>
  );
}

/** The one "more actions" affordance in the app. */
export function EllipsisIcon({ className }: IconProps) {
  return (
    <Solid className={className}>
      <circle cx="4.5" cy="10" r="1.4" />
      <circle cx="10" cy="10" r="1.4" />
      <circle cx="15.5" cy="10" r="1.4" />
    </Solid>
  );
}

/**
 * `ellipsis.circle` — the ⋯ inside a ring, which is what iOS puts in a
 * navigation bar. Reads as a button at rest without needing a hover fill, so it
 * is the right trigger on a surface where a bare ⋯ would look like decoration.
 */
export function EllipsisCircleIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className={cx("size-4 shrink-0", className)}
    >
      <circle
        cx="10"
        cy="10"
        r="8.2"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
      />
      <g fill="currentColor">
        <circle cx="6.2" cy="10" r="1.15" />
        <circle cx="10" cy="10" r="1.15" />
        <circle cx="13.8" cy="10" r="1.15" />
      </g>
    </svg>
  );
}

/* --- Actions -------------------------------------------------------------- */

export function PlusIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M10 4.5v11M4.5 10h11" />
    </Stroke>
  );
}

export function MinusIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M4.5 10h11" />
    </Stroke>
  );
}

/** Four corners: back to the whole picture (the image viewer's "fit"). */
export function FitIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M4 8V4h4M12 4h4v4M16 12v4h-4M8 16H4v-4" />
    </Stroke>
  );
}

export function SearchIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="8.75" cy="8.75" r="5.25" />
      <path d="m12.75 12.75 3.75 3.75" />
    </Stroke>
  );
}

export function CalendarIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3" y="4.5" width="14" height="12" rx="2.5" />
      <path d="M3 8.5h14M7 3v3M13 3v3" />
    </Stroke>
  );
}

export function TrashIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M4 6h12" />
      <path d="M8 6V4.5h4V6" />
      <path d="M5.5 6.5 6.2 16a1 1 0 0 0 1 .9h5.6a1 1 0 0 0 1-.9L14.5 6.5" />
      <path d="M8.5 9.5v4.5M11.5 9.5v4.5" />
    </Stroke>
  );
}

export function PencilIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M13.4 3.9a1.9 1.9 0 0 1 2.7 2.7L7.5 15.2 4 16l.8-3.5 8.6-8.6Z" />
    </Stroke>
  );
}

export function DuplicateIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="7" y="7" width="9.5" height="9.5" rx="2.2" />
      <path d="M13 4.5a1.5 1.5 0 0 0-1.5-1H5.5A2 2 0 0 0 3.5 5.5v6a1.5 1.5 0 0 0 1 1.4" />
    </Stroke>
  );
}

export function ArchiveIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3" y="4" width="14" height="3.5" rx="1.2" />
      <path d="M4.5 7.5v7A1.5 1.5 0 0 0 6 16h8a1.5 1.5 0 0 0 1.5-1.5v-7" />
      <path d="M8.25 10.5h3.5" />
    </Stroke>
  );
}

export function ImageIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="3" y="4.5" width="14" height="11" rx="2.4" />
      <circle cx="7.6" cy="8.4" r="1.2" />
      <path d="m3.6 13.4 3.3-3.1a1.4 1.4 0 0 1 1.9 0l4.5 4.2" />
      <path d="m12.3 11.5 1.3-1.2a1.4 1.4 0 0 1 1.9 0l1 .9" />
    </Stroke>
  );
}

export function ClipboardIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="4.5" y="4" width="11" height="12.5" rx="2.2" />
      <path d="M7.75 4V3.2a1 1 0 0 1 1-1h2.5a1 1 0 0 1 1 1V4" />
    </Stroke>
  );
}

export function DownloadIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M10 3.5v8.5" />
      <path d="m6.5 9 3.5 3.5L13.5 9" />
      <path d="M4 14.5v1a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5v-1" />
    </Stroke>
  );
}

/** Leave the current account/session. */
export function SignOutIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M8.5 4H5.25A1.75 1.75 0 0 0 3.5 5.75v8.5A1.75 1.75 0 0 0 5.25 16h3.25" />
      <path d="M10.5 6.5 14 10l-3.5 3.5M7.5 10H14" />
    </Stroke>
  );
}

/**
 * The bottom-sheet grab handle.
 *
 * Now the `grabber` utility (36×5, `--busee-fill-1`, fully rounded) rather than
 * a hand-measured `h-1 w-9 bg-line-strong` — the sheet's affordance is a design
 * token in `index.css`, and two descriptions of it will drift. Decorative: the
 * sheet is closable without it.
 */
export function GrabberIcon({ className }: IconProps) {
  return <span aria-hidden="true" className={cx("grabber", className)} />;
}
